// src/modules/system/errorLogService.js
//
// Admin API client for /api/error-logs. Follows the house pattern: bare axios,
// no shared instance, token read from localStorage on every call.
//
// Note: localStorage['token'] is the live key — src/modules/auth/authService.js
// (the one AuthContext uses) writes it, and every other module service reads it.
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL + '/error-logs';
const getAuthToken = () => localStorage.getItem('token');
const getHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

// Returns { success, total, limit, offset, errors[] }.
// Big columns (stack, breadcrumbs, context) are excluded server-side — fetch a
// single row with getError() to see them.
export const listErrors = async (params = {}) => {
  try {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, v);
    });
    const response = await axios.get(`${API_URL}?${q.toString()}`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error fetching error logs:', error); throw error; }
};

export const getErrorStats = async (days = 7) => {
  try {
    const response = await axios.get(`${API_URL}/stats?days=${days}`, getHeaders());
    return response.data.stats;
  } catch (error) { console.error('Error fetching error stats:', error); throw error; }
};

export const getError = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/${id}`, getHeaders());
    return response.data.error;
  } catch (error) { console.error('Error fetching error log:', error); throw error; }
};

export const setErrorStatus = async (id, status, notes) => {
  try {
    const body = {};
    if (status !== undefined) body.status = status;
    if (notes !== undefined) body.notes = notes;
    const response = await axios.patch(`${API_URL}/${id}/status`, body, getHeaders());
    return response.data.error;
  } catch (error) { console.error('Error updating error log:', error); throw error; }
};

export const bulkSetStatus = async (ids, status) => {
  try {
    const response = await axios.post(`${API_URL}/bulk-status`, { ids, status }, getHeaders());
    return response.data;
  } catch (error) { console.error('Error bulk-updating error logs:', error); throw error; }
};

export const deleteError = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/${id}`, getHeaders());
    return response.data;
  } catch (error) { console.error('Error deleting error log:', error); throw error; }
};

export const bulkDelete = async (ids) => {
  try {
    const response = await axios.post(`${API_URL}/bulk-delete`, { ids }, getHeaders());
    return response.data;
  } catch (error) { console.error('Error bulk-deleting error logs:', error); throw error; }
};

/* ------------------------------ UI helpers -------------------------------- */
// Mirrors the statusColor/statusLabel exports in timeTrackingService.js.

export const severityColor = (s) => ({
  fatal: '#b71c1c',
  error: '#e74c3c',
  warning: '#f39c12',
  info: '#3498db',
  debug: '#7f8c8d',
}[s] || '#7f8c8d');

export const statusColor = (s) => ({
  new: '#e74c3c',
  investigating: '#f39c12',
  resolved: '#84c959',
  ignored: '#7f8c8d',
}[s] || '#7f8c8d');

export const statusLabel = (s) => ({
  new: 'New',
  investigating: 'Investigating',
  resolved: 'Resolved',
  ignored: 'Ignored',
}[s] || s);

export const sourceLabel = (s) => ({
  api: 'Backend',
  mobile: 'Mobile app',
  crm: 'CRM',
  worker: 'Worker',
  webhook: 'Webhook',
}[s] || s);

export const SEVERITIES = ['fatal', 'error', 'warning', 'info', 'debug'];
export const STATUSES = ['new', 'investigating', 'resolved', 'ignored'];
export const SOURCES = ['api', 'mobile', 'crm', 'worker', 'webhook'];
export const PLATFORMS = ['ios', 'android', 'node', 'web'];

// Other errors that share a dimension with this one — same phone, same app
// session, same error class. Excludes the error itself.
export const getRelated = async (row, dimension, limit = 8) => {
  const map = {
    device: { device_id: row.device_id },
    session: { session_id: row.session_id },
    user: { user_id: row.user_id },
    type: { error_type: row.error_type },
  };
  const filter = map[dimension];
  if (!filter || !Object.values(filter)[0]) return [];
  try {
    // status/severity wide open: a related error being resolved or low-severity
    // does not make it irrelevant when you are chasing a cluster.
    const data = await listErrors({
      ...filter, limit: limit + 1, status: STATUSES.join(','), severity: SEVERITIES.join(','),
    });
    return (data.errors || []).filter((e) => e.id !== row.id).slice(0, limit);
  } catch (e) { return []; }
};

// Flatten the current filtered view to CSV. Pulls up to 2000 rows in one call
// rather than the visible page — an export of "what's on screen" is useless.
export const exportCsv = async (params = {}) => {
  const data = await listErrors({ ...params, limit: 200, offset: 0 });
  const pages = [data.errors || []];
  const total = Math.min(data.total || 0, 2000);
  for (let off = 200; off < total; off += 200) {
    const page = await listErrors({ ...params, limit: 200, offset: off });
    pages.push(page.errors || []);
  }
  const rows = pages.flat();

  const cols = ['id', 'severity', 'is_fatal', 'source', 'platform', 'error_type', 'message',
    'route', 'status_code', 'occurrences', 'affected_user_count', 'app_version', 'build_number',
    'os_name', 'os_version', 'device_model', 'release_stage', 'user_email', 'status',
    'first_seen_at', 'last_seen_at', 'fingerprint'];
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `system-errors-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return rows.length;
};

// "3 minutes ago" — errors are read relative to now far more often than by
// absolute time, especially while chasing a live crash.
export const relativeTime = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 2592000) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};
